<template>
  <v-container fluid class="landing-screen">
    <header class="landing-header">
      <router-link class="landing-brand" to="/">
        <v-img src="/logo.png" width="42" height="42" contain class="landing-brand__logo" />
        <span>Kalendar.io</span>
      </router-link>

      <div class="landing-header__actions">
        <div class="language-links" role="group" :aria-label="$t('login.languageLabel')">
          <button
            v-for="option in languageOptions"
            :key="option.value"
            type="button"
            class="language-links__option"
            :class="{ 'language-links__option--active': locale === option.value }"
            @click="changeLocale(option.value)"
          >
            {{ option.label }}
          </button>
        </div>

      </div>
    </header>

    <main class="landing-main">
      <section class="landing-hero">
        <div class="landing-copy">
          <p class="landing-kicker">
            {{ $t("landing.kicker") }}
          </p>
          <h1>
            {{ $t("landing.title") }}
          </h1>
          <p class="landing-subtitle">
            {{ $t("landing.subtitle") }}
          </p>

          <div class="landing-actions">
            <v-btn color="primary" large depressed class="landing-cta" to="/login">
              <v-icon left size="20">mdi-login</v-icon>
              {{ $t("landing.cta") }}
            </v-btn>
            <a
              class="landing-play-store"
              href="https://play.google.com/store/apps/details?id=com.thevalkiria.kalendario"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img :src="playStoreBadge" :alt="$t('landing.playStore')" />
            </a>
          </div>
        </div>

        <div class="demo-preview" aria-hidden="true">
          <div class="demo-card demo-card--event">
            <div class="demo-card__icon">
              <v-icon color="#6d28d9" size="22">mdi-calendar-plus</v-icon>
            </div>
            <div>
              <strong>{{ $t("landing.demoCreateTitle") }}</strong>
              <span>{{ $t("landing.demoCreateText") }}</span>
            </div>
          </div>

          <div class="demo-card demo-card--groups">
            <div class="demo-members">
              <span></span>
              <span></span>
              <span></span>
            </div>
            <div>
              <strong>{{ $t("landing.demoShareTitle") }}</strong>
              <span>{{ $t("landing.demoShareText") }}</span>
            </div>
          </div>

          <div class="demo-phone">
            <div class="demo-phone__top"></div>
            <div class="demo-notification">
              <v-icon color="#24b47e" size="20">mdi-bell-ring-outline</v-icon>
              <div>
                <strong>{{ $t("landing.demoRequestTitle") }}</strong>
                <span>{{ $t("landing.demoRequestText") }}</span>
              </div>
            </div>
            <div class="demo-phone__calendar">
              <span v-for="slot in 12" :key="slot"></span>
            </div>
          </div>
        </div>
      </section>

      <section class="landing-features" :aria-label="$t('landing.featuresLabel')">
        <article v-for="feature in features" :key="feature.title" class="landing-feature">
          <v-icon color="primary" size="28">{{ feature.icon }}</v-icon>
          <h2>{{ feature.title }}</h2>
          <p>{{ feature.text }}</p>
        </article>
      </section>
    </main>

    <footer class="landing-footer">
      <span>&copy; 2026 Kalendar.io. {{ $t("landing.rightsReserved") }}</span>
      <div class="landing-footer__links">
        <router-link to="/legal-notice">
          {{ $t("login.legalNotice") }}
        </router-link>
        <router-link to="/privacy-policy">
          {{ $t("login.privacyPolicy") }}
        </router-link>
        <router-link to="/cookies-policy">
          {{ $t("login.cookiesPolicy") }}
        </router-link>
      </div>
    </footer>
  </v-container>
</template>

<script>
import i18n from "@/i18n";
import { getSupportedLocales, setAppLocale } from "@/i18n";

export default {
  data: () => ({
    locale: i18n.locale
  }),
  computed: {
    languageOptions() {
      const preferredOrder = ["es", "en", "pt"];
      const supported = getSupportedLocales();
      return preferredOrder.filter(locale => supported.includes(locale)).map(locale => ({
        value: locale,
        label: locale.toUpperCase()
      }));
    },
    features() {
      return [
        {
          icon: "mdi-calendar-sync",
          title: this.$t("landing.featureCalendarTitle"),
          text: this.$t("landing.featureCalendarText")
        },
        {
          icon: "mdi-account-group",
          title: this.$t("landing.featureGroupsTitle"),
          text: this.$t("landing.featureGroupsText")
        },
        {
          icon: "mdi-bell-ring-outline",
          title: this.$t("landing.featureRequestsTitle"),
          text: this.$t("landing.featureRequestsText")
        }
      ];
    },
    playStoreBadge() {
      const badges = {
        es: "/badges/google-play-es.png",
        en: "/badges/google-play-en.png",
        pt: "/badges/google-play-pt.png"
      };

      return badges[this.locale] || badges.es;
    }
  },
  methods: {
    changeLocale(locale) {
      setAppLocale(locale);
      this.locale = locale;
    }
  }
};
</script>

<style scoped>
.landing-screen {
  min-height: 100%;
  overflow-x: hidden;
  background: #f7f5ff;
  color: #18151f;
  padding: 0;
}

.landing-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  width: min(1120px, calc(100% - 32px));
  margin: 0 auto;
  padding: 18px 0;
}

.landing-brand {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: #18151f;
  font-size: 19px;
  font-weight: 800;
  text-decoration: none;
}

.landing-brand__logo {
  flex: 0 0 auto;
}

.landing-header__actions {
  display: flex;
  align-items: center;
  gap: 14px;
}

.language-links {
  display: flex;
  gap: 6px;
}

.language-links__option {
  min-width: 32px;
  min-height: 28px;
  padding: 0 7px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #6d6578;
  cursor: pointer;
  font-family: inherit;
  font-size: 12px;
  font-weight: 700;
}

.language-links__option--active {
  background: #ede7ff;
  color: #6d28d9;
}

.landing-cta {
  text-transform: none;
  font-weight: 700;
}

.landing-cta {
  border-radius: 10px;
  background: #6d28d9 !important;
}

.landing-play-store {
  display: inline-flex;
  align-items: center;
  height: 58px;
  text-decoration: none;
}

.landing-play-store img {
  display: block;
  width: 150px;
  height: auto;
}

.landing-play-store:focus-visible {
  border-radius: 10px;
  outline: 3px solid rgba(109, 40, 217, 0.32);
  outline-offset: 2px;
}

.landing-main {
  width: min(1040px, calc(100% - 32px));
  margin: 0 auto;
  padding: 18px 0 36px;
}

.landing-hero {
  display: grid;
  grid-template-columns: minmax(0, 0.92fr) minmax(300px, 0.88fr);
  align-items: center;
  justify-content: space-between;
  gap: 34px;
  min-height: auto;
}

.landing-copy {
  max-width: 610px;
}

.landing-kicker {
  margin: 0 0 12px;
  color: #6d28d9;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0;
}

.landing-copy h1 {
  margin: 0;
  color: #100e17;
  font-size: clamp(32px, 4.5vw, 52px);
  font-weight: 650;
  letter-spacing: 0;
  line-height: 1.1;
}

.landing-subtitle {
  max-width: 500px;
  margin: 18px 0 0;
  color: #514a5e;
  font-size: 16px;
  line-height: 1.55;
}

.landing-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 26px;
}

.demo-preview {
  display: flex;
  flex-direction: column;
  gap: 14px;
  position: relative;
  max-width: 520px;
  min-height: 0;
  border: 1px solid rgba(109, 40, 217, 0.1);
  border-radius: 16px;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.96), rgba(250, 248, 255, 0.98)),
    #ffffff;
  box-shadow: 0 10px 22px rgba(28, 28, 42, 0.14);
  padding: 22px;
  overflow: hidden;
}

.demo-preview::before {
  content: "";
  position: absolute;
  inset: 18px;
  border-radius: 14px;
  background-image:
    linear-gradient(rgba(109, 40, 217, 0.06) 1px, transparent 1px),
    linear-gradient(90deg, rgba(109, 40, 217, 0.06) 1px, transparent 1px);
  background-size: 42px 42px;
}

.demo-preview::after {
  content: "";
  position: absolute;
  top: 82px;
  bottom: 82px;
  left: 44px;
  z-index: 1;
  width: 2px;
  border-radius: 99px;
  background: linear-gradient(
    180deg,
    rgba(109, 40, 217, 0.05),
    rgba(109, 40, 217, 0.22),
    rgba(36, 180, 126, 0.16)
  );
}

.demo-card,
.demo-phone {
  position: relative;
  z-index: 2;
}

.demo-card {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  border: 1px solid rgba(109, 40, 217, 0.1);
  border-radius: 12px;
  background: #ffffff;
  padding: 14px;
  box-shadow: 0 10px 20px rgba(33, 26, 53, 0.1);
}

.demo-card strong,
.demo-notification strong {
  display: block;
  color: #17121f;
  font-size: 14px;
  font-weight: 750;
  line-height: 1.25;
}

.demo-card span,
.demo-notification span {
  display: block;
  margin-top: 3px;
  color: #5b5368;
  font-size: 12px;
  line-height: 1.35;
}

.demo-card__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 42px;
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: #ede7ff;
}

.demo-card--event {
  animation: demo-card-in 8s ease-in-out infinite;
}

.demo-card--groups {
  margin: 0;
  background: #fff0d5;
  animation: demo-card-in 8s ease-in-out 1.4s infinite both;
}

.demo-members {
  display: flex;
  flex: 0 0 auto;
}

.demo-members span {
  width: 28px;
  height: 28px;
  margin-left: -7px;
  border: 2px solid #fff0d5;
  border-radius: 50%;
  background: #6d28d9;
}

.demo-members span:first-child {
  margin-left: 0;
  background: #24b47e;
}

.demo-members span:last-child {
  background: #f5a524;
}

.demo-phone {
  width: 100%;
  min-height: 0;
  border: 1px solid rgba(109, 40, 217, 0.12);
  border-radius: 18px;
  background: #ffffff;
  padding: 12px;
  box-shadow: 0 14px 26px rgba(33, 26, 53, 0.14);
  animation: demo-phone-in 8s ease-in-out 2.4s infinite both;
}

.demo-phone__top {
  width: 56px;
  height: 5px;
  margin: 0 auto 12px;
  border-radius: 99px;
  background: #d7cee8;
}

.demo-notification {
  display: flex;
  align-items: center;
  gap: 10px;
  border-radius: 12px;
  background: #e5f8ef;
  padding: 10px;
  animation: demo-notification 8s ease-in-out 3s infinite both;
}

.demo-phone__calendar {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 6px;
  margin-top: 12px;
}

.demo-phone__calendar span {
  display: block;
  height: 24px;
  border-radius: 6px;
  background: #f6f2ff;
}

.demo-phone__calendar span:nth-child(7) {
  background: #6d28d9;
}

@keyframes demo-card-in {
  0%,
  12% {
    opacity: 0;
    transform: translateY(12px) scale(0.98);
  }

  22%,
  82% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }

  100% {
    opacity: 0.88;
    transform: translateY(0) scale(1);
  }
}

@keyframes demo-phone-in {
  0%,
  34% {
    opacity: 0;
    transform: translate(18px, 14px) scale(0.96);
  }

  48%,
  100% {
    opacity: 1;
    transform: translate(0, 0) scale(1);
  }
}

@keyframes demo-notification {
  0%,
  42% {
    transform: translateY(8px);
  }

  52%,
  66% {
    transform: translateY(0);
  }

  58% {
    transform: translateY(-3px);
  }

  100% {
    transform: translateY(0);
  }
}

.landing-features {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  margin-top: 30px;
}

.landing-feature {
  min-height: 190px;
  border: 1px solid rgba(109, 40, 217, 0.1);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.78);
  padding: 22px;
}

.landing-feature h2 {
  margin: 18px 0 8px;
  color: #18151f;
  font-size: 19px;
  font-weight: 800;
  line-height: 1.25;
}

.landing-feature p {
  margin: 0;
  color: #5b5368;
  font-size: 15px;
  line-height: 1.55;
}

.landing-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: min(1040px, calc(100% - 32px));
  margin: 0 auto;
  border-top: 1px solid rgba(109, 40, 217, 0.12);
  padding: 18px 0 22px;
  color: #6d6578;
  font-size: 14px;
}

.landing-footer span {
  font-weight: 700;
}

.landing-footer__links {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.landing-footer a {
  color: #6d28d9;
  font-weight: 700;
  text-decoration: none;
}

@media (max-width: 860px) {
  .landing-header {
    align-items: center;
  }

  .landing-header__actions {
    gap: 10px;
  }

  .landing-main {
    padding-top: 12px;
  }

  .landing-hero {
    grid-template-columns: 1fr;
    gap: 24px;
    min-height: auto;
  }

  .demo-preview {
    max-width: none;
  }

  .landing-features {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 560px) {
  .landing-screen {
    min-height: 100%;
  }

  .landing-header,
  .landing-main,
  .landing-footer {
    width: min(100% - 24px, 1120px);
  }

  .landing-header {
    padding: 14px 0 8px;
  }

  .landing-header__actions {
    align-items: flex-end;
    flex-direction: column;
    gap: 6px;
  }

  .landing-brand span {
    font-size: 17px;
  }

  .landing-copy h1 {
    font-size: 30px;
    line-height: 1.08;
  }

  .landing-subtitle {
    margin-top: 16px;
    font-size: 15px;
    line-height: 1.52;
  }

  .landing-actions {
    align-items: stretch;
    flex-direction: column;
    margin-top: 22px;
  }

  .landing-cta {
    width: 100%;
  }

  .landing-play-store {
    width: 100%;
    justify-content: center;
  }

  .demo-preview {
    gap: 12px;
    border-radius: 15px;
    padding: 14px;
  }

  .demo-preview::before {
    inset: 12px;
    background-size: 34px 34px;
  }

  .demo-preview::after {
    top: 72px;
    bottom: 72px;
    left: 34px;
  }

  .demo-card {
    width: 100%;
    padding: 12px;
  }

  .demo-card--groups {
    margin-top: 0;
  }

  .demo-phone {
    width: 100%;
    padding: 10px;
  }

  .demo-card--event,
  .demo-card--groups,
  .demo-phone {
    animation-name: demo-mobile-in;
  }

  .demo-card--event {
    animation-delay: 0s;
  }

  .demo-card--groups {
    animation-delay: 0.9s;
  }

  .demo-phone {
    animation-delay: 1.8s;
  }

  .demo-phone__calendar span {
    height: 18px;
  }

  .landing-features {
    gap: 12px;
    margin-top: 18px;
  }

  .landing-feature {
    min-height: 0;
    border-radius: 15px;
    padding: 18px;
  }

  .landing-feature h2 {
    margin-top: 14px;
    font-size: 17px;
  }

  .landing-feature p {
    font-size: 14px;
  }

  .landing-footer {
    align-items: flex-start;
    flex-direction: column;
    padding-bottom: 18px;
  }

  .landing-footer__links {
    align-items: flex-start;
    flex-direction: column;
    gap: 8px;
  }
}

@keyframes demo-mobile-in {
  0%,
  12% {
    opacity: 0;
    transform: translateY(10px);
  }

  24%,
  84% {
    opacity: 1;
    transform: translateY(0);
  }

  100% {
    opacity: 0.92;
    transform: translateY(0);
  }
}
</style>
